import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CreditCard, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function ResumePayment() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/public`).then((r) => setBooking(r.data)).catch(() => setError("Booking not found."));
  }, [bookingId]);

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: window.location.origin });
      sessionStorage.setItem("cj_last_booking_id", bookingId);
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const blocked = booking && ["waitlisted", "declined", "cancelled", "expired"].includes(booking.status);
  const paid = booking?.payment_status === "paid";

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-xl px-4 py-16" data-testid="resume-payment-page">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Lock your slot</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100">Pay your deposit</h1>
        <div className="cj-card p-6 mt-8">
          {!booking && !error && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading booking…</div>}
          {booking && (
            <>
              <div className="font-display uppercase font-extrabold text-2xl text-slate-100">{booking.service_name}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">{booking.scheduled_date} · {booking.scheduled_time} · {booking.customer.address}</div>
              <div className="my-5 h-px bg-white/5" />
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Deposit due now</div>
                <div className="font-display font-extrabold text-2xl text-[#ff6b00]">${booking.deposit_amount}</div>
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Balance on completion</div>
                <div className="font-mono text-sm text-slate-300">${booking.final_balance.toFixed(2)}</div>
              </div>
              {paid ? (
                <div className="mt-6 text-emerald-300 text-sm" data-testid="resume-already-paid">Deposit already paid — you're locked in.</div>
              ) : blocked ? (
                <div className="mt-6 text-amber-300 text-sm" data-testid="resume-blocked">This booking is {booking.status.replace("_", " ")} and can't take a deposit right now.</div>
              ) : (
                <button onClick={pay} disabled={busy} data-testid="resume-pay-button" className="cj-btn-primary mt-6 w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${booking.deposit_amount} deposit <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </>
          )}
          {error && <div className="mt-4 text-sm text-red-400 font-mono uppercase tracking-wider" data-testid="resume-error">{error}</div>}
        </div>
        <Link to="/" className="mt-6 inline-block text-sm text-slate-400 hover:text-slate-200">← Back home</Link>
      </section>
      <Footer />
    </div>
  );
}
